import _defineProperty from "@babel/runtime/helpers/defineProperty";
import deepmerge from 'deepmerge';
import isObject from '../../utils/object/isObject.js';
export default class OptionsManager {
  constructor(element, schema, baseConfig) {
    var _baseConfig$debug, _baseConfig$log;

    _defineProperty(this, "__element", void 0);

    _defineProperty(this, "__values", {});

    _defineProperty(this, "__defaultValues", {
      String: '',
      Number: 0,
      Boolean: false,
      Array: () => [],
      Object: () => ({})
    });

    _defineProperty(this, "name", 'Base');

    _defineProperty(this, "debug", false);

    _defineProperty(this, "log", false);

    Object.defineProperties(this, {
      __element: {
        enumerable: false,
        writable: false,
        value: element
      },
      __values: {
        enumerable: false,
        writable: false,
        value: this.__values
      },
      __defaultValues: {
        enumerable: false,
        writable: false,
        value: this.__defaultValues
      }
    });
    this.name = baseConfig.name;
    schema.debug = {
      type: Boolean,
      default: (_baseConfig$debug = baseConfig.debug) !== null && _baseConfig$debug !== void 0 ? _baseConfig$debug : false
    };
    schema.log = {
      type: Boolean,
      default: (_baseConfig$log = baseConfig.log) !== null && _baseConfig$log !== void 0 ? _baseConfig$log : false
    };
    Object.entries(schema).forEach(_ref => {
      var [name, config] = _ref;

      this.__register(name, config);
    });
    return this;
  }

  __register(name, config) {
    var hasDefaultValue = !OptionsManager.types.includes(config);
    var type = hasDefaultValue ? config.type : config;

    if (!OptionsManager.types.includes(type)) {
      throw new Error("The \"".concat(name, "\" option has an invalid type. The allowed types are: String, Number, Boolean, Array and Object."));
    }

    var defaultValue = hasDefaultValue ? config.default : this.__defaultValues[type.name];

    if ((type === Array || type === Object) && typeof defaultValue !== 'function') {
      throw new Error("The default value for options of type \"".concat(type.name, "\" must be returned by a function."));
    }

    Object.defineProperty(this, name, {
      get: () => {
        return this.get(name, type, defaultValue);
      },
      set: value => {
        this.set(name, type, value, defaultValue);
      },
      enumerable: true
    });
  }

  get(name, type, defaultValue) {
    var propertyName = OptionsManager.__getPropertyName(name);

    var hasProperty = this.__hasProperty(propertyName);

    if (type === Boolean) {
      if (defaultValue) {
        var negatedPropertyName = OptionsManager.__getPropertyName(name, 'No');

        var hasNegatedProperty = this.__hasProperty(negatedPropertyName);

        return !hasNegatedProperty;
      }

      return hasProperty || defaultValue;
    }

    var value = this.__element.dataset[propertyName];

    if (type === Number) {
      return hasProperty ? Number(value) : defaultValue;
    }

    if (type === Array || type === Object) {
      var val = deepmerge(defaultValue(), hasProperty ? JSON.parse(value) : this.__defaultValues[type.name]());

      if (!this.__values[name]) {
        this.__values[name] = val;
      }

      return this.__values[name];
    }

    return hasProperty ? value : defaultValue;
  }

  set(name, type, value, defaultValue) {
    var propertyName = OptionsManager.__getPropertyName(name);

    if (value.constructor.name !== type.name) {
      var val = Array.isArray(value) || isObject(value) ? JSON.stringify(value) : value;
      throw new TypeError("The \"".concat(val, "\" value for the \"").concat(name, "\" option must be of type \"").concat(type.name, "\""));
    }

    switch (type) {
      case Boolean:
        if (defaultValue) {
          var negatedPropertyName = OptionsManager.__getPropertyName(name, 'No');

          if (value) {
            delete this.__element.dataset[negatedPropertyName];
          } else {
            this.__element.dataset[negatedPropertyName] = '';
          }
        } else if (value) {
          this.__element.dataset[propertyName] = '';
        } else {
          delete this.__element.dataset[propertyName];
        }

        break;

      case Array:
      case Object:
        this.__values[name] = value;
        break;

      default:
        this.__element.dataset[propertyName] = value;
    }
  }

  __hasProperty(prop) {
    return typeof this.__element.dataset[prop] !== 'undefined';
  }

  static __getPropertyName(name) {
    var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    var key = name + prefix;

    if (OptionsManager.__propertyNameCache.has(key)) {
      return OptionsManager.__propertyNameCache.get(key);
    }

    var propertyName = "option".concat(prefix).concat(name.replace(/^\w/, c => c.toUpperCase()));

    OptionsManager.__propertyNameCache.set(key, propertyName);

    return propertyName;
  }

}

_defineProperty(OptionsManager, "types", [String, Number, Boolean, Array, Object]);

_defineProperty(OptionsManager, "__propertyNameCache", new Map());